import { Fragment } from 'react';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Send } from 'domains/campaigns/types';
import {
    ChannelSegmentFilter,
    RecordTypeFilter,
    SegmentFilter,
    SegmentFilterCategorySpecificField,
    SegmentFilterState,
} from 'domains/segments/types';
import DeleteButton from 'domains/core/components/DeleteButton';
import { FilterGroupNode, MAX_NESTING_DEPTH } from '../types';
import Filter from '../Filter';
import theme from 'theme';
import { brandColors } from 'colors';

const matchTypes = ['AND', 'OR'];

const getGroupStyles = (depth: number) => ({
    ...(depth > 0 && {
        border: `1px solid ${theme.palette.other.divider}`,
        borderRadius: '8px',
        padding: '28px',
        marginTop: '8px',
        marginBottom: '8px',
        backgroundColor: depth % 2 === 1 ? brandColors.stone : 'white',
    }),
});

export type FilterGroupProps = {
    group: FilterGroupNode;
    path: number[];
    depth: number;
    // Filter metadata passed through to leaf Filters:
    demographicFilters: SegmentFilter[];
    engagementFilters: SegmentFilter[] | ChannelSegmentFilter[];
    eventRecordTypeFilters: RecordTypeFilter[];
    sends: Send[];
    handleDatetimeError: (filterId: string, error: boolean) => void;
    isMaxFilterLimit: boolean;
    // Tree mutation callbacks:
    onAddFilter: (path: number[]) => void;
    onAddGroup: (path: number[]) => void;
    onUpdateFilter: (
        path: number[],
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>,
        resetPreview?: boolean
    ) => void;
    onRemoveNode: (path: number[]) => void;
    onUpdateMatchType: (path: number[], matchType: 'AND' | 'OR') => void;
};

const addButtonSx = {
    background: theme.palette.action.active,
    '&:hover': {
        background: theme.palette.action.selected,
    },
};

const addButtonSxForNestedChildren = {
    background: 'white',
    boxShadow: 'none',
    color: theme.palette.secondary.main,
    border: `2px solid ${theme.palette.secondary.main}`,
    '&:hover': {
        background: brandColors.stone,
        boxShadow: 'none',
    },
};

const FilterGroup = ({
    group,
    path,
    depth,
    demographicFilters,
    engagementFilters,
    eventRecordTypeFilters,
    sends,
    handleDatetimeError,
    isMaxFilterLimit,
    onAddFilter,
    onAddGroup,
    onUpdateFilter,
    onRemoveNode,
    onUpdateMatchType,
}: FilterGroupProps) => (
    <Stack sx={getGroupStyles(depth)}>
        {group.children.map((child, index) => (
            <Fragment key={child.id}>
                {child.type === 'filter' ? (
                    <Filter
                        filter={child.filter}
                        demographicFilters={demographicFilters}
                        engagementFilters={engagementFilters}
                        eventRecordTypeFilters={eventRecordTypeFilters}
                        sends={sends}
                        handleDatetimeError={handleDatetimeError}
                        onUpdate={(filterData, resetPreview) =>
                            onUpdateFilter([...path, index], filterData, resetPreview)
                        }
                        onRemove={() => onRemoveNode([...path, index])}
                    />
                ) : (
                    <Box position="relative">
                        <DeleteButton
                            onClick={() => onRemoveNode([...path, index])}
                            tooltipTitle="Delete filter group"
                            sx={{
                                position: 'absolute',
                                top: '8px',
                                right: '-22px',
                                zIndex: 1,
                                svg: { height: '20px', width: '20px' },
                            }}
                        />
                        <FilterGroup
                            group={child.group}
                            path={[...path, index]}
                            depth={depth + 1}
                            demographicFilters={demographicFilters}
                            engagementFilters={engagementFilters}
                            eventRecordTypeFilters={eventRecordTypeFilters}
                            sends={sends}
                            handleDatetimeError={handleDatetimeError}
                            isMaxFilterLimit={isMaxFilterLimit}
                            onAddFilter={onAddFilter}
                            onAddGroup={onAddGroup}
                            onUpdateFilter={onUpdateFilter}
                            onRemoveNode={onRemoveNode}
                            onUpdateMatchType={onUpdateMatchType}
                        />
                    </Box>
                )}

                {/* AND/OR toggle between siblings */}
                {index < group.children.length - 1 && (
                    <ToggleButtonGroup
                        aria-label="match type"
                        exclusive
                        onChange={(_, newValue) => {
                            if (newValue !== null) {
                                onUpdateMatchType(path, newValue);
                            }
                        }}
                        style={{ margin: '16px' }}
                        value={group.matchType}
                    >
                        {matchTypes.map((type) => (
                            <ToggleButton
                                aria-label={type}
                                key={type}
                                sx={{
                                    textTransform: 'lowercase',
                                    color: 'grey.700', // default text
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main', // selected background
                                        color: 'common.white', // selected text
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: 'primary.dark', // selected + hover
                                    },
                                }}
                                value={type}
                            >
                                {type}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </Fragment>
        ))}

        {/* Add filter / Add filter group buttons */}
        <Box mt={2} display="flex" gap={2}>
            <Button
                disabled={isMaxFilterLimit}
                variant="contained"
                onClick={() => onAddFilter(path)}
                size="small"
                sx={depth > 0 ? addButtonSxForNestedChildren : addButtonSx}
            >
                + Add filter
            </Button>
            {depth < MAX_NESTING_DEPTH && (
                <Button
                    disabled={isMaxFilterLimit}
                    variant="contained"
                    onClick={() => onAddGroup(path)}
                    size="small"
                    sx={depth > 0 ? addButtonSxForNestedChildren : addButtonSx}
                    // sx={ addButtonSxForNestedChildren}
                >
                    + Add filter group
                </Button>
            )}
        </Box>
    </Stack>
);

export default FilterGroup;
